/**
 * SupportApiClient
 * 
 * Handles all support ticket and feedback related API operations
 */

import { BaseApiClient } from './BaseApiClient';

export interface SupportTicket {
  _id: string;
  ticketNumber: string;
  userId?: string;
  email: string;
  username?: string;
  subject: string;
  message: string;
  category: 'feedback' | 'bug' | 'feature' | 'billing' | 'technical' | 'other';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'waiting_response' | 'resolved' | 'closed';
  
  // Metadata
  source?: string;
  userAgent?: string;
  appVersion?: string;
  
  // Messages thread
  messages?: TicketMessage[];
  
  // Assignment
  assignedTo?: string;
  assignedToName?: string;
  assignedAt?: Date;
  resolvedBy?: string;
  resolvedByName?: string;
  resolvedAt?: Date;
  resolutionNotes?: string;
  satisfactionRating?: number;
  satisfactionComment?: string;
  
  // Timestamps
  createdAt: string;
  updatedAt: string;
  lastActivityAt?: string;
  firstResponseAt?: string;
  
  // Tags
  tags?: string[];
  
  // Related entities
  relatedTransactionId?: string;
  relatedMerchantId?: string;
  relatedOrderId?: string;
  
  // Metrics
  messageCount?: number;
  responseCount?: number;
  reopenCount?: number;
  escalated?: boolean;
}

export interface TicketMessage {
  _id: string;
  authorId: string;
  authorName: string;
  authorRole: 'user' | 'admin' | 'support' | 'system';
  message: string;
  attachments?: TicketAttachment[];
  createdAt: string;
  isInternal?: boolean;
}

export interface TicketAttachment {
  filename: string;
  url?: string;
  data?: string; // Base64 for upload
  mimeType: string;
  size?: number;
}

export interface CreateTicketData {
  email: string;
  subject: string;
  message: string;
  category?: SupportTicket['category'];
  priority?: SupportTicket['priority'];
  attachments?: TicketAttachment[];
  relatedTransactionId?: string;
  relatedMerchantId?: string;
  appVersion?: string;
}

export interface TicketListResponse {
  tickets: SupportTicket[];
  total: number;
  hasMore: boolean;
}

export interface TicketStats {
  totalTickets: number;
  openTickets: number;
  resolvedToday: number;
  avgResponseTime: number;
  avgResolutionTime: number;
  satisfactionScore: number;
}

export interface SupportStats {
  summary: TicketStats;
  byCategory: Record<string, number>;
  byPriority: Record<string, number>;
  byAgent?: Array<{
    agentId: string;
    agentName: string;
    assigned: number;
    resolved: number;
    avgResponseTime: number;
    satisfactionScore: number;
  }>;
  timeline?: Array<{
    date: string;
    created: number;
    resolved: number;
    responseTime: number;
  }>;
}

export class SupportApiClient extends BaseApiClient {
  // ============================================
  // User Endpoints
  // ============================================

  /**
   * Submit a new support ticket or feedback
   */
  async createTicket(data: CreateTicketData): Promise<{
    success: boolean;
    ticket: SupportTicket;
    message: string;
  }> {
    // Add user agent and app version
    const enrichedData = {
      ...data,
      userAgent: navigator.userAgent,
      appVersion: (typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : '1.0.0')
    };

    return this.post<{
      success: boolean;
      ticket: SupportTicket;
      message: string;
    }>('/support/tickets', enrichedData);
  }

  /**
   * Get current user's tickets
   */
  async getMyTickets(params?: {
    status?: SupportTicket['status'];
    limit?: number;
    skip?: number;
    sort?: string;
  }): Promise<TicketListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.skip) queryParams.append('skip', params.skip.toString());
    if (params?.sort) queryParams.append('sort', params.sort);

    return this.get<TicketListResponse>(`/support/tickets/my?${queryParams}`);
  }

  /**
   * Get ticket details
   */
  async getTicket(ticketId: string): Promise<{ ticket: SupportTicket }> {
    return this.get<{ ticket: SupportTicket }>(`/support/tickets/${ticketId}`);
  }

  /**
   * Add a message to a ticket
   */
  async addMessage(ticketId: string, data: {
    message: string;
    attachments?: TicketAttachment[];
  }): Promise<{
    success: boolean;
    message: TicketMessage;
  }> {
    return this.post<{
      success: boolean;
      message: TicketMessage;
    }>(`/support/tickets/${ticketId}/messages`, data);
  }

  /**
   * Rate ticket resolution
   */
  async rateTicket(ticketId: string, rating: number, comment?: string): Promise<{
    success: boolean;
  }> {
    return this.post<{ success: boolean }>(`/support/tickets/${ticketId}/rate`, {
      rating,
      comment
    });
  }

  // ============================================
  // Admin Endpoints
  // ============================================

  /**
   * Get all tickets (admin)
   */
  async getAllTickets(params?: {
    status?: SupportTicket['status'];
    category?: SupportTicket['category'];
    priority?: SupportTicket['priority'];
    assignedTo?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    skip?: number;
    sort?: string;
  }): Promise<TicketListResponse & { stats: TicketStats }> {
    const queryParams = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    return this.get<TicketListResponse & { stats: TicketStats }>(
      `/admin/support/tickets?${queryParams}`
    );
  }

  /**
   * Update ticket (admin)
   */
  async updateTicket(ticketId: string, updates: Partial<SupportTicket>): Promise<{
    success: boolean;
    ticket: SupportTicket;
  }> {
    return this.put<{
      success: boolean;
      ticket: SupportTicket;
    }>(`/admin/support/tickets/${ticketId}`, updates);
  }

  /**
   * Assign ticket to agent (admin)
   */
  async assignTicket(ticketId: string, agentId: string): Promise<{
    success: boolean;
  }> {
    return this.put<{ success: boolean }>(`/admin/support/tickets/${ticketId}`, {
      assignedTo: agentId,
      status: 'in_progress'
    });
  }

  /**
   * Resolve ticket (admin)
   */
  async resolveTicket(ticketId: string, resolutionNotes: string): Promise<{
    success: boolean;
  }> {
    return this.put<{ success: boolean }>(`/admin/support/tickets/${ticketId}`, {
      status: 'resolved',
      resolutionNotes
    });
  }

  /**
   * Add internal note (admin)
   */
  async addInternalNote(ticketId: string, note: string): Promise<{
    success: boolean;
    message: TicketMessage;
  }> {
    return this.post<{
      success: boolean;
      message: TicketMessage;
    }>(`/support/tickets/${ticketId}/messages`, {
      message: note,
      isInternal: true
    });
  }

  /**
   * Get support statistics (admin)
   */
  async getStats(period: '24h' | '7d' | '30d' | 'month' | 'year' = '30d'): Promise<SupportStats> {
    return this.get<SupportStats>(`/admin/support/stats?period=${period}`);
  }

  /**
   * Bulk update tickets (admin)
   */
  async bulkUpdate(ticketIds: string[], updates: Partial<SupportTicket>): Promise<{
    success: boolean;
    updated: number;
  }> {
    return this.post<{
      success: boolean;
      updated: number;
    }>('/admin/support/tickets/bulk', {
      ticketIds,
      updates
    });
  }

  /**
   * Export tickets (admin)
   */
  async exportTickets(params: {
    format: 'csv' | 'json' | 'pdf';
    status?: SupportTicket['status'];
    dateFrom?: string;
    dateTo?: string;
  }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        queryParams.append(key, value.toString());
      }
    });

    const response = await fetch(`${this.baseURL}/admin/support/tickets/export?${queryParams}`, {
      method: 'GET',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      throw new Error('Failed to export tickets');
    }

    return response.blob();
  }

  /**
   * Search tickets
   */
  async searchTickets(query: string): Promise<TicketListResponse> {
    return this.get<TicketListResponse>(`/admin/support/tickets?search=${encodeURIComponent(query)}`);
  }
}
